import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from "@material-ui/core";
import Label from "../../Label";
import {
  Close as CloseIcon,
  Home as HomeIcon,
  OpenInNew as OpenInNewIcon,
  ImageNotSupported as ImageNotSupportedIcon,
  Person as PersonIcon,
  Call as CallIcon,
  WhatsApp as WhatsAppIcon
} from "@material-ui/icons";
import { fetchListingById } from "../../../services/signaturesListings";
import type { ProcessedListingData } from "../../../@types/sharedSchema";
import {
  getLocalPhoneNumberIfIsraeliNumber,
  toInternationalFormat
} from "../../../utils/Haim/phoneNumbersUtils";

const SOURCE_LABELS: Record<string, string> = {
  yad2: "Yad2",
  madlan: "Madlan"
};

type ListingPreviewModalProps = {
  open: boolean;
  onClose: () => void;
  listingId: string | null;
};

const ilsFormatter = new Intl.NumberFormat("he-IL", {
  style: "currency",
  currency: "ILS",
  maximumFractionDigits: 0
});

const formatAddress = (listing: ProcessedListingData | null): string => {
  if (!listing?.address) return "";
  const a = listing.address;
  const street = [a.street, a.houseNumber].filter((v) => v !== undefined && v !== "").join(" ");
  return [street, a.city].filter(Boolean).join(", ");
};

export default function ListingPreviewModal({ open, onClose, listingId }: ListingPreviewModalProps) {
  const { t } = useTranslation();
  const [listing, setListing] = useState<ProcessedListingData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !listingId) return;
    let cancelled = false;
    setLoading(true);
    fetchListingById(listingId)
      .then((l) => {
        if (!cancelled) setListing(l);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, listingId]);

  const imageUrl = listing?.coverImageUrl || listing?.imageUrls?.[0];
  const address = formatAddress(listing);
  const externalUrl = listing?.originalListingUrl;
  const isNonWebot = !!listing?.listingOrigin && listing.listingOrigin !== "webot";
  const sourceLabel = listing?.listingOrigin ? SOURCE_LABELS[listing.listingOrigin] : undefined;

  const rawPhone = listing?.sellerPhoneNumber;
  const displayPhone = rawPhone
    ? getLocalPhoneNumberIfIsraeliNumber(rawPhone, false) ?? rawPhone
    : undefined;
  const telPhone = rawPhone ? toInternationalFormat(rawPhone) ?? rawPhone : undefined;
  const whatsappDigits = telPhone ? telPhone.replace(/\D/g, "") : undefined;
  const whatsappText = externalUrl
    ? `${t("listingPreview.whatsAppQuestion")} ${externalUrl}`
    : undefined;
  const whatsappHref = whatsappDigits
    ? `https://wa.me/${whatsappDigits}${
        whatsappText ? `?text=${encodeURIComponent(whatsappText)}` : ""
      }`
    : undefined;

  const showPublisher =
    isNonWebot && !!(listing?.sellerName || listing?.agencyName || displayPhone);

  return (
    // Elevated zIndex so this dialog layers ABOVE the signatures detail
    // side-panel (which sits at zIndex 2001). Otherwise the modal opens
    // behind the panel when launched from the panel's listing chips.
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" sx={{ zIndex: 2100 }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{t("listingPreview.title")}</span>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !listing ? (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
            {t("listingPreview.notFound")}
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Box
              sx={{
                width: "100%",
                aspectRatio: "16 / 9",
                bgcolor: "grey.200",
                borderRadius: 1,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {imageUrl ? (
                // eslint-disable-next-line jsx-a11y/alt-text
                <img
                  src={imageUrl}
                  alt={address}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <ImageNotSupportedIcon sx={{ fontSize: 48, color: "grey.500" }} />
              )}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <HomeIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight="bold">
                {address || t("listingPreview.noAddress")}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              {listing.price != null && (
                <Label color="primary" variant="ghost">
                  {ilsFormatter.format(listing.price)}
                </Label>
              )}
              {listing.roomsCount != null && (
                <Label color="info" variant="ghost">
                  {t("listingPreview.rooms", { count: listing.roomsCount })}
                </Label>
              )}
              {listing.squareMeter != null && (
                <Label color="warning" variant="ghost">
                  {t("listingPreview.squareMeter", { sqm: listing.squareMeter })}
                </Label>
              )}
              {listing.propertyType && (
                <Label color="success" variant="ghost">
                  {listing.propertyType}
                </Label>
              )}
            </Stack>

            {showPublisher && (
              <Box sx={{ p: 2, borderRadius: 1, border: 1, borderColor: "divider" }}>
                <Stack spacing={1}>
                  <Typography variant="overline" color="text.secondary">
                    {t("listingPreview.publisher")}
                  </Typography>
                  {listing!.sellerName && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={600}>
                        {listing!.sellerName}
                      </Typography>
                    </Stack>
                  )}
                  {listing!.agencyName && (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
                      {listing!.agencyName}
                    </Typography>
                  )}
                  {displayPhone && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ pt: 0.5 }}>
                      <Button
                        variant="contained"
                        startIcon={<CallIcon />}
                        href={`tel:${telPhone}`}
                        dir="ltr"
                        sx={{
                          bgcolor: "info.lighter",
                          color: "info.darker",
                          boxShadow: "none",
                          "&:hover": { bgcolor: "info.light", boxShadow: "none" }
                        }}
                      >
                        {displayPhone}
                      </Button>
                      {whatsappHref && (
                        <Button
                          variant="contained"
                          startIcon={<WhatsAppIcon />}
                          component="a"
                          href={whatsappHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            bgcolor: "success.lighter",
                            color: "success.darker",
                            boxShadow: "none",
                            "&:hover": { bgcolor: "success.light", boxShadow: "none" }
                          }}
                        >
                          WhatsApp
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}

            {externalUrl && isNonWebot && (
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                component="a"
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {sourceLabel
                  ? t("listingPreview.viewOnSource", { source: sourceLabel })
                  : t("listingPreview.openSource")}
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
